
interface Booking {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  booking_date: string;
  booking_time: string;
  status: string;
  payment_status: string;
  payment_id: string;
  message: string;
  created_at: string;
  currency: string;
  amount_paid: number;
}

interface StatsOverviewProps {
  bookings: Booking[];
}

export default function StatsOverview({ bookings }: StatsOverviewProps) {
  const today = new Date().toISOString().split('T')[0];

  const totalBookings = bookings.length;
  const confirmedBookings = bookings.filter((b) => b.payment_status === 'paid' || b.status === 'confirmed').length;
  const pendingBookings = bookings.filter((b) => b.status === 'pending').length;
  const todayBookings = bookings.filter((b) => b.booking_date === today).length;

  const totalRevenue = bookings
    .filter((b) => b.payment_status === 'paid')
    .reduce((sum, b) => {
      if (b.currency === 'NGN') return sum + (b.amount_paid || 0);
      return sum + (b.amount_paid || 0);
    }, 0);

  const stats = [
    {
      label: 'Total Bookings',
      value: totalBookings,
      icon: 'ri-calendar-line',
      color: 'bg-emerald-50 text-emerald-600',
      iconBg: 'bg-emerald-100',
    },
    {
      label: 'Confirmed',
      value: confirmedBookings,
      icon: 'ri-check-double-line',
      color: 'bg-teal-50 text-teal-600',
      iconBg: 'bg-teal-100',
    },
    {
      label: 'Pending',
      value: pendingBookings,
      icon: 'ri-time-line',
      color: 'bg-amber-50 text-amber-600',
      iconBg: 'bg-amber-100',
    },
    {
      label: "Today\\'s Bookings",
      value: todayBookings,
      icon: 'ri-calendar-todo-line',
      color: 'bg-rose-50 text-rose-600',
      iconBg: 'bg-rose-100',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${stat.iconBg}`}>
              <i className={`${stat.icon} text-base sm:text-lg ${stat.color.split(' ')[1]}`}></i>
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">{stat.value}</p>
          <p className="text-[11px] sm:text-xs text-gray-500 font-medium">{stat.label.replace("\\'", "'")}</p>
        </div>
      ))}

      {totalRevenue > 0 && (
        <div className="col-span-2 lg:col-span-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-[11px] sm:text-xs font-medium mb-1">Total Revenue (Mixed Currencies)</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-2xl"></i>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
